import React, { Fragment, useContext, useRef, useState } from 'react';
import Image from 'next/image';
import { Dialog, Disclosure, Transition } from '@headlessui/react';
import { oneLine } from 'common-tags';

import { Add, ChevronDown, Less, Menu } from '@/icons';
import { NextLink } from '@/atoms';
import { Button } from '@/molecules';
import { ToastCenter } from '@/organisms';

import { InitPropsProvider } from '@/components/providers/initPropsProvider';
import { imageTransformer } from '@/lib/graphql/transformers';
import { useHeaderSticky, useRouteChangeStart } from '@/lib/hooks';
import { routes } from '@/lib/routes';
import Toolbar from '../desktop/Toolbar';
import FooterMobile from './Footer';
import { HeaderMobile } from './Header/Header';

import type { TypeImageGraphQL } from '@/lib/graphql/types';
import type { FunctionComponent } from 'react';
import type { MobileMenuProps } from './types';

/**
 * MobileMenu
 */
export const MobileMenu: FunctionComponent<MobileMenuProps> = ({
  open,
  setOpen,
  setOpenModal,
  setLoginModalOpen,
}: MobileMenuProps) => {
  const [subMenuOpen, setSubmenuOpen] = useState(-1);
  const {
    options: { ajustesGenerales },
    menus: {
      primaryMenu: { menus },
    },
  } = useContext(InitPropsProvider);

  const logo = imageTransformer(
    ajustesGenerales.logoMobile as TypeImageGraphQL,
  );

  useRouteChangeStart(() => setOpen(false));

  const ref = useRef<HTMLDivElement>(null);

  useHeaderSticky(ref);

  return (
    <nav
      ref={ref}
      aria-label="Top"
      className="sticky top-0 z-40 transition-all"
    >
      <div className="max-w-7xl border-b border-b-primary-100 bg-bg-primary px-4 py-[6px] sm:px-6 lg:hidden lg:border-t lg:border-t-primary-900 lg:px-8">
        <div className="flex h-[46px] items-center lg:py-4">
          <div className="flex flex-1 items-center justify-between lg:items-start lg:gap-16">
            <div className="flex flex-row items-center gap-6">
              <button
                type="button"
                className="lg:hidden"
                onClick={() => {
                  setOpen(true);
                }}
              >
                <span className="sr-only">Open menu</span>
                <Menu className="h-6 w-6" aria-hidden="true" />
              </button>
              {logo && (
                <NextLink href={routes.index}>
                  <a
                    className="relative h-[42px] w-[140px]"
                    aria-label="Ir a la home"
                  >
                    <Image
                      src={logo.src}
                      alt={logo.alt}
                      layout="fill"
                      objectFit="contain"
                      objectPosition="center"
                      draggable="false"
                      priority
                    />
                  </a>
                </NextLink>
              )}
            </div>

            <Toolbar
              setLoginModalOpen={setLoginModalOpen}
              setMenuOpen={setOpenModal}
            />
          </div>
        </div>
      </div>

      <Transition.Root show={open} as={Fragment} unmount={false}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 flex lg:hidden"
          onClose={setOpen}
          unmount={false}
          open={open}
        >
          <Transition.Child
            unmount={false}
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <Transition.Child
            unmount={false}
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div
              className={oneLine`relative z-[100] flex h-full w-full flex-col bg-white px-4 py-6 
              ${subMenuOpen === -1 ? 'overflow-y-auto' : ''}`}
            >
              <HeaderMobile
                setOpen={() => {
                  setOpen(false);
                }}
              />
              <div className="flex flex-grow flex-col py-4">
                {menus?.map((menu: any, index: number) =>
                  !menu.column && menu?.parentMenuLink?.url ? (
                    <NextLink
                      href={menu?.parentMenuLink?.url ?? '/'}
                      key={index}
                    >
                      <a className="u-body u-body--l py-4 normal-case">
                        {menu.parentMenu}
                      </a>
                    </NextLink>
                  ) : (
                    <React.Fragment key={index}>
                      <div
                        className="flex w-full cursor-pointer justify-between py-4"
                        onClick={() => setSubmenuOpen(index)}
                      >
                        <p className="u-body u-body--l normal-case">
                          {menu.parentMenu}
                        </p>

                        <ChevronDown
                          className="-rotate-90"
                          color="var(--tw-color-secondary-500)"
                        />
                      </div>

                      {menu.column && (
                        <Transition
                          show={index === subMenuOpen}
                          as={Fragment}
                          enter="transition ease-in-out duration-300 transform"
                          enterFrom="-translate-x-full"
                          enterTo="translate-x-0"
                          leave="transition ease-in-out duration-300 transform"
                          leaveFrom="translate-x-0"
                          leaveTo="-translate-x-full"
                        >
                          <div className="absolute left-0 top-0 z-[100] flex h-full w-full flex-col overflow-y-auto bg-white px-4 py-6">
                            <HeaderMobile
                              setOpen={() => {
                                setOpen(false);
                                setSubmenuOpen(-1);
                              }}
                            />

                            <div
                              onClick={() => setSubmenuOpen(-1)}
                              className="flex cursor-pointer items-end justify-start gap-6 border-b border-b-primary-100 py-4"
                            >
                              <ChevronDown
                                className="relative top-[1px] rotate-90"
                                color="var(--tw-color-secondary-500)"
                                height={24}
                                width={24}
                              />

                              <p className="u-body u-body--l normal-case">
                                {menu.parentMenu}
                              </p>
                            </div>

                            <div className="flex flex-grow flex-col items-start py-4">
                              {menu.column.map((column: any) =>
                                column.bloque.map(
                                  (bloque: any, index: number) =>
                                    bloque.subMenuMobile && bloque.menuPages ? (
                                      <Disclosure key={index}>
                                        {({ open }) => (
                                          <>
                                            <Disclosure.Button
                                              as="div"
                                              className="flex w-full items-center justify-between py-4"
                                            >
                                              <p className="u-body u-body--m normal-case text-primary-600">
                                                {bloque.title}
                                              </p>
                                              {open ? (
                                                <Less
                                                  color="#F68217"
                                                  width={24}
                                                  height={24}
                                                />
                                              ) : (
                                                <Add
                                                  color="#F68217"
                                                  width={24}
                                                  height={24}
                                                />
                                              )}
                                            </Disclosure.Button>

                                            <Disclosure.Panel
                                              unmount={false}
                                              className="flex w-full flex-col p-4"
                                              as="ul"
                                            >
                                              {bloque?.menuPages.map(
                                                (
                                                  menuPage: any,
                                                  index: number,
                                                ) => (
                                                  <li
                                                    key={index}
                                                    className="w-full py-4"
                                                  >
                                                    <NextLink
                                                      href={
                                                        menuPage?.pageUrl
                                                          ?.url ?? '/'
                                                      }
                                                    >
                                                      <a className="u-body u-body--m normal-case text-primary-400">
                                                        {menuPage.pageTitle}
                                                      </a>
                                                    </NextLink>
                                                  </li>
                                                ),
                                              )}
                                            </Disclosure.Panel>
                                          </>
                                        )}
                                      </Disclosure>
                                    ) : (
                                      <NextLink
                                        key={index}
                                        href={bloque?.page?.url ?? '/'}
                                      >
                                        <a className="u-body u-body--m w-full py-4 normal-case text-primary-600">
                                          {bloque.title}
                                        </a>
                                      </NextLink>
                                    ),
                                ),
                              )}
                            </div>
                            <FooterMobile
                              socialNetworks={ajustesGenerales.socialNetworks}
                              button={
                                menu?.parentMenuLink?.url && (
                                  <NextLink
                                    href={menu.parentMenuLink.url}
                                    passHref
                                  >
                                    <Button
                                      label={menu.parentMenu}
                                      size="large-full"
                                      style="primary"
                                      as="a"
                                    />
                                  </NextLink>
                                )
                              }
                            />
                          </div>
                        </Transition>
                      )}
                    </React.Fragment>
                  ),
                )}
              </div>

              <FooterMobile
                socialNetworks={ajustesGenerales.socialNetworks}
                button={
                  <NextLink href={routes.joinBigCrafters} passHref>
                    <Button
                      label="únete a bigcrafters®"
                      size="large-full"
                      style="secondary"
                      as="a"
                    />
                  </NextLink>
                }
              />
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>
      <ToastCenter className="lg:hidden" />
    </nav>
  );
};
